import { useMutation, useQueryClient } from '@tanstack/react-query';
import { takeoverConversation, type TakeoverResult } from '@/lib/api';
import { conversationQueryKey } from './useConversation';

/**
 * useTakeover — owner is taking the wheel for this thread. Posts to
 * `/pwa/conversations/:id/takeover` which tags the contact `shelby_paused`
 * in GHL and writes a 30-day KV pause flag so SMS Shelby's inbound
 * pipeline skips AI generation for any subsequent message on this thread.
 *
 * Ernie 2026-05-12 — replaces the older "Take over" button behavior that
 * just opened the GHL contact in a new browser tab.
 */
export function useTakeover(slug: string, conversationId: string) {
  const qc = useQueryClient();
  const key = conversationQueryKey(slug, conversationId);

  return useMutation<TakeoverResult, Error, void>({
    mutationFn: () => takeoverConversation({ slug, conversationId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key });
      // 2026-06-01 — also invalidate the conversations list query so the
      // Drip badge on the row clears immediately. Worker-side fixes
      // (c589d92 / 7683516) drop the trigger tags + add quote_followup_stop,
      // but without this list-level invalidation React Query keeps serving
      // the pre-takeover list payload and is_workflow_active stays true on
      // the row. Partial key matches all list variants (needs / quoted /
      // all / search). Mirrors useMarkBooked / useMarkResolved pattern.
      void qc.invalidateQueries({ queryKey: ['conversations', slug] });
    },
  });
}
